// view/listNav.js — createListNav: a generic "list navigator" control  ◄ [center] ►  with an optional
// dropdown list. Google-/data-source-free: it reads an active index via getIndex() and reports back via
// onSelect(key) / onSet(value). Consumers (PDF page nav, mapType switch, gallery nav) wire it to their truth.
//
// Extracted (frontend_view-state-base, listNav pilot) from topoMapCore/tools/mapTypeControl.js: the generic
// listnav mechanics (cycle, is-edge, dropdown, click-outside, stable-width label grid) live here; the map
// specifics (variant checkbox, narrow observer, google events, map.controls) stay in the mapType consumer.
//
// Returns { element, update(), setWrap(bool), destroy() }. Uses core/createDisposers for teardown.

import { createDisposers } from '../core/disposers.js';

const PREV_SVG  = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const NEXT_SVG  = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CARET_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

// Grösse als Baustein: die Consumer sitzen in unterschiedlich hohen Leisten (Karte, Galerie, Popup-Fussleiste),
// und alle Segmente EINER Navigation müssen dieselbe Stufe tragen — deshalb einmal zentral eingesetzt.
const ARROW_CLS = (size) => `topo2-btn topo2-btn--outline topo2-btn--${size} topo2-btn--icon`;
const BAR_CLS   = (size) => `topo2-btn topo2-btn--outline topo2-btn--bar topo2-btn--${size}`;

// Größenkontext für die NICHT-Knopf-Teile (die Eingabegruppe mit Feld und Suffix). topo2 führt dafür eigene
// Klassen, die nur die Größen-Tokens setzen — und sie heißen anders als die Knopf-Modifier:
// Knopf `--extrasmall` ↔ Kontext `topo2-xsmall`, Knopf `--small` ↔ Kontext `topo2-small`.
// Die Klasse sitzt am CONTAINER, damit alles darin dieselben Maße erbt, statt sie je Teil zu wiederholen.
// `medium` fehlt bewusst: eine Kontext-Klasse dafür gibt es in topo2 nicht, und sie wäre auch überflüssig —
// medium IST der :root-Standard, ohne Klasse stimmen die Maße also schon.
const SIZE_CONTEXT_CLS = { extrasmall: 'topo2-xsmall', small: 'topo2-small' };

export function createListNav({
    items = [],                 // [{ key, label }] — dropdown entries + (label mode) the stable-width grid
    center = 'label',           // 'label' (options grid) | 'input' (editable field + suffix)
    suffix = '',                // input mode: string OR (activeIndex) => string, e.g. () => `/${items.length}`
    expandVia = 'label',        // open the list via: 'label' click | 'arrow' (own caret button) | 'both'
    wrap = false,               // false = no wrap (arrows dim at edges, is-edge); true = wrap around. setWrap() to change.
    openUp = false,             // true = Liste klappt NACH OBEN auf (für Consumer am unteren Rand, z.B. die
                                // PDF-Navigation in der Popup-Fußleiste — nach unten liefe sie aus dem Fenster)
    size = 'small',             // topo2-Größenstufe ALLER Segmente: 'extrasmall' | 'small' | 'medium'
    solid = false,              // true = solid variant (gefüllter Akzent-Rahmen, weißer Inhalt; nur das Input weiß-bg)
    getIndex = () => -1,        // () => active index (the consumer's truth)
    getValue = null,            // input mode: () => string shown in the field (default: String(activeIndex + 1))
    onSelect = () => {},        // (key) => void — list click OR arrow cycle (selects the neighbour)
    onSet = () => {},           // (value) => void — input mode, on Enter
    onToggle = () => {},        // (expanded) => void — when the dropdown opens/closes (host can react)
    prevLabel = '',             // a11y: aria-label + title for the prev / next arrows
    nextLabel = '',
    centerLabel = '',           // a11y: aria-label for the label button (label mode)
} = {}) {
    const disposers = createDisposers();
    let expanded = false;
    let wrapMode = !!wrap;

    const mk = (tag, cls) => { const e = document.createElement(tag); if (cls) { e.className = cls; } return e; };
    const on = (node, ev, fn) => { node.addEventListener(ev, fn); disposers.add(() => node.removeEventListener(ev, fn)); };

    const container = mk('div', 'topo2-listnav topo2-btn-listnav');
    if (SIZE_CONTEXT_CLS[size]) { container.classList.add(SIZE_CONTEXT_CLS[size]); }
    if (solid) { container.classList.add('topo2-listnav--solid'); }
    if (openUp) { container.classList.add('topo2-listnav--up'); }

    // ── Pfeile: wählen den Nachbarn (kein eigener State — onSelect trägt es zur Wahrheit) ──────────
    const makeArrow = (svg, cls, delta, label) => {
        const b = mk('button', `topo2-listnav-arrow ${cls} ${ARROW_CLS(size)}`);
        b.type = 'button';
        b.innerHTML = svg;
        if (label) { b.title = label; b.setAttribute('aria-label', label); }
        on(b, 'click', () => cycle(delta));
        return b;
    };
    const prev = makeArrow(PREV_SVG, 'topo2-listnav-prev', -1, prevLabel);
    const next = makeArrow(NEXT_SVG, 'topo2-listnav-next', 1, nextLabel);

    // ── Zentrum: Options-Grid (label) ODER editierbares Feld (input) ───────────────────────────────
    const centerWrap = mk('div', 'topo2-listnav-centerwrap');
    let labelOpts = [];
    let inputEl = null;
    let suffixEl = null;

    if (center === 'input') {
        // KEINE eigene Größenklasse mehr: die Gruppe erbt den Größenkontext vom Container (s. SIZE_CONTEXT_CLS).
        // Vorher stand hier `topo2-small` fest — seit die Größe eine Option ist, hätte die Eingabegruppe sonst
        // 30px behalten, während die Knöpfe daneben auf 19px gingen (User 2026-09-25).
        const group = mk('span', 'topo2-listnav-inputgroup');
        inputEl = mk('input', 'topo2-listnav-input');   // transparent inside the group; the group draws the frame
        inputEl.type = 'text';
        inputEl.inputMode = 'numeric';
        on(inputEl, 'mousedown', (e) => e.stopPropagation());   // nicht als Außen-Klick werten
        on(inputEl, 'keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); onSet(inputEl.value.trim()); } });
        suffixEl = mk('span', 'topo2-listnav-suffix');
        group.appendChild(inputEl);
        group.appendChild(suffixEl);
        centerWrap.appendChild(group);
    } else {
        const labelBtn = mk('button', `topo2-listnav-label ${BAR_CLS(size)}`);
        labelBtn.type = 'button';
        if (centerLabel) { labelBtn.setAttribute('aria-label', centerLabel); }
        const grid = mk('span', 'topo2-listnav-labelgrid');   // alle Labels überlagert → Breite = breitestes, stabil
        labelOpts = items.map((it) => { const s = mk('span', 'topo2-listnav-opt'); s.textContent = it.label; grid.appendChild(s); return s; });
        labelBtn.appendChild(grid);
        if (expandVia === 'label' || expandVia === 'both') { on(labelBtn, 'click', toggle); }
        centerWrap.appendChild(labelBtn);
    }

    // ── Optionaler Dropdown-Pfeil-Button (expandVia arrow/both) ───────────────────────────────────
    let caretBtn = null;
    if ((expandVia === 'arrow' || expandVia === 'both') && items.length) {
        caretBtn = mk('button', `topo2-listnav-caret ${ARROW_CLS(size)}`);
        caretBtn.type = 'button';
        caretBtn.innerHTML = CARET_SVG;
        on(caretBtn, 'click', toggle);
        centerWrap.appendChild(caretBtn);
    }

    // ── Dropdown-Liste (absolut, unter dem Zentrum; ändert die Zeilenhöhe nicht) ──────────────────
    const list = mk('div', 'topo2-listnav-list');
    const listButtons = items.map((it) => {
        const b = mk('button', `topo2-listnav-item ${BAR_CLS(size)}`);
        b.type = 'button';
        b.textContent = it.label;
        b.dataset.key = it.key;
        on(b, 'click', () => { onSelect(it.key); setExpanded(false); });
        list.appendChild(b);
        return b;
    });
    centerWrap.appendChild(list);

    container.appendChild(prev);
    container.appendChild(centerWrap);
    container.appendChild(next);

    function setExpanded(v) {
        if (expanded === v) { return; }
        expanded = v;
        onToggle(expanded);
        update();
    }
    function toggle() { setExpanded(!expanded); }

    function cycle(delta) {
        const n = items.length;
        if (!n) { return; }
        const i = getIndex();
        let j = i + delta;
        if (wrapMode) { j = (j + n) % n; }
        else if (i < 0 || j < 0 || j >= n) { return; }   // kein Umlauf: an den Rändern no-op (Pfeil ist is-edge)
        onSelect(items[j].key);
    }

    // Klick außerhalb → einklappen (mousedown, damit es vor anderen Interaktionen greift)
    const onOutside = (e) => { if (expanded && !container.contains(e.target)) { setExpanded(false); } };
    document.addEventListener('mousedown', onOutside);
    disposers.add(() => document.removeEventListener('mousedown', onOutside));

    function update() {
        const n = items.length;
        const i = getIndex();
        if (center === 'input' && inputEl) {
            inputEl.value = getValue ? getValue() : String(i + 1);
            suffixEl.textContent = typeof suffix === 'function' ? suffix(i) : suffix;
        } else {
            labelOpts.forEach((s, k) => { s.style.visibility = (k === i) ? 'visible' : 'hidden'; });
        }
        list.style.display = expanded ? '' : 'none';
        listButtons.forEach((b, k) => b.setAttribute('aria-pressed', (expanded && k === i) ? 'true' : 'false'));
        prev.classList.toggle('is-edge', !wrapMode && i <= 0);
        next.classList.toggle('is-edge', !wrapMode && i >= n - 1);
        container.classList.toggle('is-expanded', expanded);
        if (caretBtn) { caretBtn.classList.toggle('is-open', expanded); }
    }

    update();

    return {
        element: container,
        update,
        setWrap(v) { wrapMode = !!v; update(); },
        setNarrow(v) { container.classList.toggle('is-narrow', !!v); },   // vertical stack for narrow hosts
        // Locks every control while the consumer waits (e.g. a PDF page change waits for the save, User 2026-10-08).
        setDisabled(v) {
            container.classList.toggle('is-disabled', !!v);
            container.querySelectorAll('button, input').forEach((el) => { el.disabled = !!v; });
            if (v) { setExpanded(false); }
        },
        destroy() { disposers.dispose(); },
    };
}
